import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatTimeAMPM(timeStr) {
  if (!timeStr) return "";
  if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
    return timeStr;
  }
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  const hour = parseInt(parts[0], 10);
  const min = parts[1];
  if (isNaN(hour)) return timeStr;
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const displayMin = min.substring(0, 2);
  return `${displayHour}:${displayMin} ${ampm}`;
}

/**
 * Computes the effective membership status by checking if endDate has passed
 * in the org's local timezone. Mirrors backend compute_membership_status().
 *
 * The DB status field never auto-updates to "Expired" when endDate passes,
 * so this function must be used everywhere a membership status is displayed.
 *
 * @param {object|null} ms  - membership object { status, endDate }
 * @param {string} orgTimezone - IANA timezone string e.g. "Asia/Kolkata"
 * @returns {string} "Expired" | "Active" | "Partial" | "Inactive" | "New"
 */
export function getEffectiveMembershipStatus(ms, orgTimezone) {
  if (!ms) return "New";
  const stored = ms.status || "New";
  // Only Active and Partial can flip to Expired by date
  if (stored !== "Active" && stored !== "Partial") return stored;
  if (!ms.endDate) return stored;

  try {
    const tz = orgTimezone || "UTC";
    const endDateStr = ms.endDate.slice(0, 10); // YYYY-MM-DD

    // Build end-of-day (23:59:59) on endDate in org timezone
    // We use toLocaleString to get the current time in the org timezone, then compare
    const endOfDayLocal = new Date(`${endDateStr}T23:59:59`);
    // Get now in org timezone as a comparable date
    const nowStr = new Date().toLocaleString("en-CA", { timeZone: tz, hour12: false });
    // en-CA gives "YYYY-MM-DD, HH:MM:SS" format — reliable cross-browser
    const nowLocal = new Date(nowStr.replace(", ", "T"));

    const endStr = new Date(endDateStr).toLocaleString("en-CA", { timeZone: tz, hour12: false });
    // End of day: same date but time 23:59:59
    const [endDatePart] = endStr.split(",");
    const endOfDayInTz = new Date(`${endDatePart.trim()}T23:59:59`);

    if (nowLocal > endOfDayInTz) return "Expired";
  } catch {
    // Fallback: simple UTC comparison
    try {
      const end = new Date(ms.endDate + "T23:59:59Z");
      if (new Date() > end) return "Expired";
    } catch { /* ignore */ }
  }
  return stored;
}
